import { useState, useEffect, useMemo, useRef } from 'react';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { KanbanCartao, KanbanQuadro, KanbanRaia, NivelPrioridadeKanban } from '@pieltec/contracts';
import { useKanbanQuadro } from './kanban/use-kanban-quadro';
import { KanbanColunaComponent } from './kanban/kanban-coluna';
import { KanbanFilaEntrada } from './kanban/kanban-fila-entrada';
import { KanbanCartaoPainel } from './kanban/kanban-cartao-painel';
import { KanbanFiltros } from './kanban/kanban-filtros';
import { aplicarFiltros, filtrosVazios, opcoesDeFiltro, type FiltrosKanban } from '../../lib/kanban/filtros';
import { carregarFiltros, salvarFiltros } from '../../lib/kanban/preferencias';
import { AlertCircle, Loader2, RefreshCw, LayoutDashboard, WifiOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { alternarColapso } from '../../lib/kanban/volume';
import { useKanbanSse } from './kanban/use-kanban-sse';
import { criarDebounce } from '../../lib/kanban/sse';
import { lerOpDoDeepLink, localizarCartaoDaOp } from '../../lib/kanban/deep-link';
import { obterSessaoAtual } from '../../lib/session';
import {
  alvoSoltura,
  aplicarMovimentoOtimista,
  aplicarReordenacaoOtimista,
  aplicarVersaoComando,
  localizarCartao,
  localizarRaia,
  raiaAceitaCartao,
  raiaCompativel,
  reavaliarMovimentoConfirmado,
  resolverSoltura,
  type DestinoMover,
} from '../../lib/kanban/movimento';
import { useKanbanComandos, type ResultadoComando } from './kanban/use-kanban-comandos';
import { KanbanAcoesContext, type KanbanAcoes } from './kanban/kanban-acoes-context';
import { useToast } from '../../components/ui/toast';
import { RevisaoFuturaDialog } from '../../components/desenhos/revisao-futura-dialog';


interface ConfirmacaoMover {
  cartao: KanbanCartao;
  raiaDestinoId: string;
  celulaDestino: string;
  novaPosicao: number;
  indiceDestino: number;
}

function textoFalha(resposta: Exclude<ResultadoComando, { tipo: 'ok' }>): string {
  if (resposta.tipo === 'conflito' || resposta.codigo) return resposta.message;
  return 'Não foi possível mover o cartão. Tente novamente.';
}

export default function KanbanPage() {
  const toast = useToast();
  const { quadro, setQuadro, loading, erro, carregar, concluidoDias, setConcluidoDias, geracaoRef } = useKanbanQuadro();
  const [colunasRecolhidas, setColunasRecolhidas] = useState<Set<string>>(new Set());
  const [raiasRecolhidas, setRaiasRecolhidas] = useState<Set<string>>(new Set());
  const [podeGerenciar, setPodeGerenciar] = useState(false);
  const [podeEncerrarParcial, setPodeEncerrarParcial] = useState(false);
  const [podeConsultarProducao, setPodeConsultarProducao] = useState(false);
  const [arraste, setArraste] = useState<{ cartao: KanbanCartao; raiaId: string } | null>(null);
  const [raiaEmFoco, setRaiaEmFoco] = useState<string | null>(null);
  const [confirmacao, setConfirmacao] = useState<ConfirmacaoMover | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [cartaoAberto, setCartaoAberto] = useState<KanbanCartao | null>(null);
  const [osDesenhoAberta, setOsDesenhoAberta] = useState<string | null>(null);
  const [filtros, setFiltros] = useState<FiltrosKanban>(filtrosVazios);
  const userIdRef = useRef<string | null>(null);
  // Cartões com comando em voo: um segundo gesto sobre o mesmo cartão usaria a versão antiga (409 evitável).
  const emVooRef = useRef<Set<string>>(new Set());
  const quadroRef = useRef<KanbanQuadro | null>(quadro);
  quadroRef.current = quadro;
  const { reordenar, mover, alterarPrioridade, resolverConflito, reordenarFilaEntrada, desbloquear } = useKanbanComandos();
  const [ponteiroGrosso, setPonteiroGrosso] = useState(false);
  // Deep-link do detalhe do PV (12-21): `?op={numero}` lido uma vez no mount.
  const [opDoLink] = useState(() => lerOpDoDeepLink(window.location.search));
  const deepLinkTratadoRef = useRef(false);
  const realceRef = useRef<{ quadro: number | null; timer: number | null; elemento: Element | null }>({
    quadro: null,
    timer: null,
    elemento: null,
  });

  // Distância e atraso evitam que o arrasto roube o scroll do tablet.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (!vivo) return;
      setPodeGerenciar(sessao.autenticado && sessao.usuario.permissions.includes('kanban:gerenciar'));
      setPodeEncerrarParcial(sessao.autenticado && sessao.usuario.permissions.includes('producao:gerenciar'));
      setPodeConsultarProducao(sessao.autenticado && sessao.usuario.permissions.includes('producao:consultar'));
      if (!sessao.autenticado) return;
      userIdRef.current = sessao.usuario.userId;
      // D-33: filtros salvos por usuário no navegador (IndexedDB).
      void carregarFiltros(sessao.usuario.userId).then((salvos) => {
        if (vivo) setFiltros(salvos);
      });
    });
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(() => {
    const consulta = window.matchMedia('(pointer: coarse)');
    const aplicar = () => setPonteiroGrosso(consulta.matches);
    aplicar();
    consulta.addEventListener('change', aplicar);
    return () => consulta.removeEventListener('change', aplicar);
  }, []);

  const notificar = (tom: 'erro' | 'aviso', texto: string) => {
    if (tom === 'erro') toast.error({ title: texto });
    else toast.warning({ title: texto });
  };

  // Recarga que falha com o quadro já na tela: aviso passageiro em vez de um painel que espreme o quadro.
  useEffect(() => {
    if (erro && quadro) notificar('erro', erro);
  }, [erro, quadro]);

  // Deep-link "Ver no Kanban": com o quadro carregado, localiza o cartão da OP, abre o painel lateral
  // existente, rola até o cartão e o realça por alguns segundos. Sem cartão: aviso âmbar.
  useEffect(() => {
    if (!opDoLink || !quadro || deepLinkTratadoRef.current) return;
    deepLinkTratadoRef.current = true;
    const cartao = localizarCartaoDaOp(quadro, opDoLink);
    if (!cartao) {
      notificar('aviso', `Cartão da OP ${opDoLink} não está no quadro.`);
      return;
    }
    setCartaoAberto(cartao);
    const seletor = `[data-cartao-chave="${CSS.escape(cartao.chave)}"]`;
    const realce = realceRef.current;
    let tentativas = 0;
    // O cartão pode demorar um quadro a existir no DOM (raia virtualizada): tenta por até ~0,5 s.
    const procurar = () => {
      const elemento = document.querySelector(seletor);
      if (!elemento) {
        tentativas += 1;
        if (tentativas < 30) realce.quadro = window.requestAnimationFrame(procurar);
        return;
      }
      elemento.scrollIntoView({ block: 'center', inline: 'center' });
      elemento.classList.add('ring-2', 'ring-(--color-focus)', 'rounded-md');
      realce.elemento = elemento;
      realce.timer = window.setTimeout(() => {
        elemento.classList.remove('ring-2', 'ring-(--color-focus)', 'rounded-md');
        realce.elemento = null;
      }, 4000);
    };
    realce.quadro = window.requestAnimationFrame(procurar);
  }, [opDoLink, quadro]);

  useEffect(() => {
    const realce = realceRef.current;
    return () => {
      if (realce.quadro !== null) window.cancelAnimationFrame(realce.quadro);
      if (realce.timer !== null) window.clearTimeout(realce.timer);
      realce.elemento?.classList.remove('ring-2', 'ring-(--color-focus)', 'rounded-md');
    };
  }, []);

  const acoes = useMemo<KanbanAcoes>(
    () => ({
      atualizar: () => void carregar(),
      notificar,
    }),
    [carregar, toast],
  );

  const aoMudarFiltros = (proximos: FiltrosKanban) => {
    setFiltros(proximos);
    if (userIdRef.current) void salvarFiltros(userIdRef.current, proximos);
  };

  // Filtros só escondem cartões na tela; contagens e WIP seguem o servidor (D-28). Recalcula a cada SSE/refetch.
  const quadroExibido = useMemo(() => (quadro ? aplicarFiltros(quadro, filtros) : null), [quadro, filtros]);
  const opcoes = useMemo(() => (quadro ? opcoesDeFiltro(quadro) : { clientes: [], celulas: [] }), [quadro]);

  const carregarDebounced = useMemo(() => criarDebounce(() => void carregar(), 300), [carregar]);

  const { status } = useKanbanSse({
    url: '/api/v1/kanban/stream',
    onEvento: () => void carregarDebounced(),
    onReconectar: () => void carregar(),
  });

  const aoIniciarArraste = (event: DragStartEvent) => {
    const atual = quadroRef.current;
    if (!atual) return;
    const origem = localizarCartao(atual, String(event.active.id));
    if (!origem) return;
    setArraste({ cartao: origem.cartao, raiaId: origem.raiaId });
  };

  const aoSobre = (event: DragOverEvent) => {
    const atual = quadroRef.current;
    if (!atual || !event.over) {
      setRaiaEmFoco(null);
      return;
    }
    setRaiaEmFoco(alvoSoltura(atual, String(event.over.id))?.raiaId ?? null);
  };

  const limparArraste = () => {
    setArraste(null);
    setRaiaEmFoco(null);
  };

  /**
   * Desfaz o gesto otimista com segurança: o snapshot só volta se nenhum quadro novo do servidor
   * chegou desde o gesto (senão ele descartaria dados mais recentes); a recarga decide o resto.
   */
  const reverterOtimista = (anterior: KanbanQuadro, geracaoInicial: number) => {
    if (geracaoRef.current === geracaoInicial) setQuadro(anterior);
    void carregar();
  };

  const concluirComando = async (
    anterior: KanbanQuadro,
    pedido: Promise<ResultadoComando>,
    manterDialogo: boolean,
    chave: string,
  ) => {
    const geracaoInicial = geracaoRef.current;
    const resposta = await pedido;
    emVooRef.current.delete(chave);
    if (resposta.tipo === 'ok') {
      const resultado = resposta.resultado;
      if (resultado) {
        setQuadro((vigente) => (vigente ? aplicarVersaoComando(vigente, resultado) : vigente));
        if (resultado.avisos.length > 0) {
          notificar('aviso', resultado.avisos.map((aviso) => aviso.mensagem).join(' · '));
        }
      }
      setConfirmacao(null);
      setConfirmando(false);
      // Uma carga iniciada antes do comando terminar poderia trazer o estado pré-comando; a nova
      // carga invalida qualquer resposta anterior e reflete o estado final do servidor.
      void carregar();
      return;
    }
    reverterOtimista(anterior, geracaoInicial);
    notificar('erro', textoFalha(resposta));
    setConfirmando(false);
    if (resposta.tipo === 'conflito') {
      setConfirmacao(null);
      return;
    }
    if (!manterDialogo) setConfirmacao(null);
  };

  const aoSoltar = (event: DragEndEvent) => {
    const atual = quadroRef.current;
    limparArraste();
    if (!atual || !podeGerenciar || !event.over) return;
    const origem = localizarCartao(atual, String(event.active.id));
    const destino = alvoSoltura(atual, String(event.over.id));
    if (!origem || !destino) return;
    executarDecisao(atual, origem, destino);
  };

  const executarDecisao = (
    atual: KanbanQuadro,
    origem: NonNullable<ReturnType<typeof localizarCartao>>,
    destino: { raiaId: string; indice: number },
  ) => {
    const decisao = resolverSoltura(
      { origem: { raiaId: origem.raiaId, indice: origem.indice }, destino },
      atual,
    );
    if (decisao.tipo === 'nenhum') return;
    if (decisao.tipo === 'recusado') {
      notificar('erro', decisao.motivo);
      return;
    }
    if (decisao.tipo === 'mover' && decisao.exigeConfirmacao) {
      setConfirmacao({
        cartao: origem.cartao,
        raiaDestinoId: destino.raiaId,
        celulaDestino: decisao.celulaDestino,
        novaPosicao: decisao.novaPosicao,
        indiceDestino: destino.indice,
      });
      return;
    }

    const chave = origem.cartao.chave;
    if (emVooRef.current.has(chave)) {
      notificar('aviso', 'Aguarde a conclusão do movimento anterior deste cartão.');
      return;
    }
    emVooRef.current.add(chave);

    const anterior = atual;
    if (decisao.tipo === 'reordenar') {
      setQuadro(aplicarReordenacaoOtimista(atual, origem.cartao.chave, origem.raiaId, decisao.novaPosicao));
      void concluirComando(anterior, reordenar(origem.cartao, decisao.novaPosicao), false, chave);
      return;
    }
    if (decisao.tipo === 'reordenar_fila_entrada') {
      setQuadro(aplicarReordenacaoOtimista(atual, origem.cartao.chave, origem.raiaId, decisao.novaPosicao));
      void concluirComando(anterior, reordenarFilaEntrada(decisao.opId, decisao.novaPosicao), false, chave);
      return;
    }
    setQuadro(aplicarMovimentoOtimista(atual, decisao.osId, destino.raiaId, decisao.novaPosicao));
    void concluirComando(
      anterior,
      mover(origem.cartao, decisao.celulaDestino, decisao.novaPosicao, false),
      false,
      chave,
    );
  };

  const aoMoverPeloMenu = (cartao: KanbanCartao, destinoMenu: DestinoMover) => {
    const atual = quadroRef.current;
    if (!atual || !podeGerenciar) return;
    const origem = localizarCartao(atual, cartao.chave);
    if (!origem) return;
    const raiaDestino = origem.coluna.raias.find(
      (raia) => raia.tipo === 'CELULA' && raia.ativa && raia.celulaCodigo === destinoMenu.celulaCodigo,
    );
    if (!raiaDestino) return;
    executarDecisao(atual, origem, { raiaId: raiaDestino.id, indice: raiaDestino.cartoes.length });
  };

  const aoPrioridade = (cartao: KanbanCartao, nivel: NivelPrioridadeKanban) => {
    const anterior = quadroRef.current;
    if (!anterior) return;
    const geracaoInicial = geracaoRef.current;
    setQuadro({
      ...anterior,
      colunas: anterior.colunas.map((coluna) => ({
        ...coluna,
        raias: coluna.raias.map((raia) => ({
          ...raia,
          cartoes: raia.cartoes.map((item) =>
            item.opId === cartao.opId ? { ...item, nivelPrioridade: nivel } : item,
          ),
        })),
      })),
    });
    void (async () => {
      const resposta = await alterarPrioridade(cartao.opId, nivel);
      if (resposta.tipo === 'ok') {
        void carregar();
        return;
      }
      reverterOtimista(anterior, geracaoInicial);
      notificar('erro', textoFalha(resposta));
    })();
  };

  const aoResolverConflito = (cartao: KanbanCartao) => {
    const anterior = quadroRef.current;
    if (!anterior) return;
    const geracaoInicial = geracaoRef.current;
    setQuadro({
      ...anterior,
      colunas: anterior.colunas.map((coluna) => ({
        ...coluna,
        raias: coluna.raias.map((raia) => ({
          ...raia,
          cartoes: raia.cartoes.map((item) =>
            item.osId === cartao.osId ? { ...item, conflito: null } : item,
          ),
        })),
      })),
    });
    void (async () => {
      const resposta = await resolverConflito(cartao);
      if (resposta.tipo === 'ok') {
        void carregar();
        return;
      }
      reverterOtimista(anterior, geracaoInicial);
      notificar('erro', textoFalha(resposta));
    })();
  };

  const aoDesbloquear = async (cartao: KanbanCartao) => {
    if (!cartao.osId) return;
    const resposta = await desbloquear(cartao.osId, cartao.versao);
    if (resposta.tipo === 'ok') {
      void carregar();
    } else {
      notificar('erro', textoFalha(resposta));
    }
  };

  const confirmarMovimento = () => {
    const atual = quadroRef.current;
    if (!confirmacao || !atual || confirmando) return;
    // O quadro pode ter mudado por SSE com o diálogo aberto: reavalia cartão, versão, raia e posição.
    const movimento = reavaliarMovimentoConfirmado(atual, {
      chave: confirmacao.cartao.chave,
      raiaDestinoId: confirmacao.raiaDestinoId,
      indiceDestino: confirmacao.indiceDestino,
    });
    if (movimento.tipo === 'abortar') {
      setConfirmacao(null);
      notificar('erro', movimento.motivo);
      return;
    }
    const chave = movimento.cartao.chave;
    if (emVooRef.current.has(chave)) {
      notificar('aviso', 'Aguarde a conclusão do movimento anterior deste cartão.');
      return;
    }
    emVooRef.current.add(chave);
    setConfirmando(true);
    setQuadro(
      aplicarMovimentoOtimista(atual, movimento.cartao.osId ?? '', movimento.raiaDestinoId, movimento.novaPosicao),
    );
    void concluirComando(
      atual,
      mover(movimento.cartao, movimento.celulaDestino, movimento.novaPosicao, true),
      true,
      chave,
    );
  };

  const avaliarRaia = (raia: KanbanRaia) => {
    if (!arraste || !quadro) return { aceitaSoltura: false, compativel: false, emFoco: false };
    const origem = localizarRaia(quadro, arraste.raiaId);
    const codigo = raia.celulaCodigo;
    return {
      aceitaSoltura: raiaAceitaCartao(quadro, arraste.cartao.chave, raia.id),
      compativel: origem?.raia.tipo === 'A_ALOCAR' && codigo !== null && raiaCompativel(arraste.cartao, codigo),
      emFoco: raiaEmFoco === raia.id,
    };
  };

  return (
    <KanbanAcoesContext.Provider value={acoes}>
    <div
      className="kanban-page flex min-h-0 min-w-0 flex-col h-full overflow-hidden bg-slate-100"
      data-density={ponteiroGrosso ? 'comfortable' : undefined}
    >
      <div className="flex justify-between items-center px-6 py-4 bg-white border-b border-slate-200 shrink-0 shadow-sm">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-100 text-blue-700 rounded-md">
            <LayoutDashboard className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 tracking-tight">Quadro Kanban</h1>
            <p className="text-sm text-slate-500">Monitoramento da produção e fluxo contínuo</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void carregar()} disabled={loading} className="gap-2 text-slate-600">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {status === 'nao_autorizado' && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-2 flex items-center justify-center gap-2 text-red-800 text-sm font-medium z-10 shrink-0">
          <WifiOff className="h-4 w-4" />
          Tempo real indisponível: sessão expirada ou acesso negado. Entre novamente e recarregue a página.
        </div>
      )}

      {status === 'reconectando' && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-2 flex items-center justify-center gap-2 text-yellow-800 text-sm font-medium z-10 shrink-0">
          <WifiOff className="h-4 w-4" />
          Conexão em tempo real perdida. Reconectando…
        </div>
      )}

      {quadro && <KanbanFiltros filtros={filtros} opcoes={opcoes} onMudar={aoMudarFiltros} />}

      <div className="flex-1 flex min-h-0 min-w-0 overflow-x-auto overflow-y-hidden" tabIndex={0} role="region" aria-label="Colunas do quadro Kanban">
        {erro && !quadro && (
          <div className="p-6 max-w-2xl overflow-y-auto w-full">
            <div className="flex flex-col items-center justify-center p-6 bg-red-50 text-red-700 rounded-md border border-red-200 gap-2 text-center">
              <AlertCircle className="h-8 w-8 text-red-500 mb-2" />
              <h3 className="font-semibold text-lg">Erro ao carregar</h3>
              <p>{erro}</p>
              <Button onClick={() => void carregar()} className="mt-4" variant="outline">
                Tentar Novamente
              </Button>
            </div>
          </div>
        )}

        {!erro && loading && !quadro && (
          <div className="flex h-full w-full p-4 gap-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex flex-col min-w-[320px] w-[320px] shrink-0 h-full bg-slate-50 border border-slate-200 rounded-sm">
                <div className="p-3 border-b border-slate-200 ui-skeleton h-12 w-full" />
                <div className="p-2 flex flex-col gap-4 mt-2">
                  <div className="flex flex-col gap-2">
                    <div className="ui-skeleton h-6 w-1/3 mb-1" />
                    <div className="ui-skeleton h-24 w-full rounded-md" />
                    <div className="ui-skeleton h-24 w-full rounded-md" />
                    <div className="ui-skeleton h-24 w-full rounded-md" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="ui-skeleton h-6 w-1/3 mb-1" />
                    <div className="ui-skeleton h-24 w-full rounded-md" />
                    <div className="ui-skeleton h-24 w-full rounded-md" />
                    <div className="ui-skeleton h-24 w-full rounded-md" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {quadro && quadroExibido && (
          <DndContext
            sensors={podeGerenciar ? sensors : []}
            collisionDetection={closestCorners}
            onDragStart={aoIniciarArraste}
            onDragOver={aoSobre}
            onDragCancel={limparArraste}
            onDragEnd={(event) => void aoSoltar(event)}
          >
          <div className="flex h-full w-full">
            {quadroExibido.colunas.map((coluna) => {
              if (coluna.tipo === 'FILA_ENTRADA') {
                return (
                  <KanbanFilaEntrada
                    key={coluna.id}
                    coluna={coluna}
                    recolhida={colunasRecolhidas.has(coluna.id)}
                    onAlternarRecolher={() => setColunasRecolhidas(prev => alternarColapso(prev, coluna.id))}
                    podeArrastar={podeGerenciar}
                    loading={loading}
                    quadro={quadro}
                    onMover={(cartao, destino) => void aoMoverPeloMenu(cartao, destino)}
                    onPrioridade={(cartao, nivel) => void aoPrioridade(cartao, nivel)}
                    onResolverConflito={(cartao) => void aoResolverConflito(cartao)}
                    onDesbloquear={(cartao) => void aoDesbloquear(cartao)}
                    onSelecionarCartao={setCartaoAberto}
                  />
                );
              }
              return (
                <KanbanColunaComponent
                  key={coluna.id}
                  coluna={coluna}
                  recolhida={colunasRecolhidas.has(coluna.id)}
                  onAlternarRecolher={() => setColunasRecolhidas(prev => alternarColapso(prev, coluna.id))}
                  raiasRecolhidas={raiasRecolhidas}
                  onAlternarRaia={(raiaId) => setRaiasRecolhidas(prev => alternarColapso(prev, raiaId))}
                  onSelecionarCartao={setCartaoAberto}
                  podeArrastar={podeGerenciar}
                  avaliarRaia={avaliarRaia}
                  quadro={quadro}
                  onMover={(cartao, destino) => void aoMoverPeloMenu(cartao, destino)}
                  onPrioridade={(cartao, nivel) => void aoPrioridade(cartao, nivel)}
                  onResolverConflito={(cartao) => void aoResolverConflito(cartao)}
                  onDesbloquear={(cartao) => void aoDesbloquear(cartao)}
                  concluidoDias={concluidoDias}
                  onMudarConcluidoDias={setConcluidoDias}
                />
              );
            })}
          </div>
          </DndContext>
        )}
        <KanbanCartaoPainel
          cartao={cartaoAberto}
          podeGerenciar={podeGerenciar}
          onFechar={() => setCartaoAberto(null)}
          onVerDesenho={setOsDesenhoAberta}
          podeEncerrarParcial={podeEncerrarParcial}
          podeConsultarProducao={podeConsultarProducao}
          onRecarregarQuadro={() => void carregar()}
        />
        <RevisaoFuturaDialog
          aberto={osDesenhoAberta !== null}
          onAbertoChange={(aberto) => { if (!aberto) setOsDesenhoAberta(null); }}
          osId={osDesenhoAberta ?? undefined}
        />
        <AlertDialog
          open={confirmacao !== null}
          onOpenChange={(aberto) => {
            if (!aberto && !confirmando) setConfirmacao(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Mover cartão</AlertDialogTitle>
              <AlertDialogDescription>
                Este cartão está pausado. Mover para outra célula retoma a fila na célula de destino. Confirmar movimentação?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={confirmando}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-white hover:bg-destructive/90"
                disabled={confirmando}
                onClick={(event) => {
                  event.preventDefault();
                  void confirmarMovimento();
                }}
              >
                {confirmando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                Mover mesmo assim
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
    </KanbanAcoesContext.Provider>
  );
}
